/**
 * User-initiated companion reconnect, shared by the input-bar wrench, the
 * disconnect toast, and failed starts. All feedback updates the single
 * disconnect toast. The guard is held through verification so repeated
 * clicks cannot trip the 3-failure escalation within seconds, and the
 * automatic loop waits for it; the retry budget re-arms once it settles.
 */
import { translate } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { useCompanionStore } from "@/stores/companion-store";
import {
  clearCompanionDisconnectToast,
  showCompanionDisconnectToast,
} from "./connection-health/companion-disconnect-toast";
import { restartCompanion } from "./companion-restart";
import { rearmCompanionAutoReconnect } from "./use-companion-reconnect";
import { waitForCompanionConnected } from "./wait-for-companion-connected";

const VERIFY_WINDOW_MS = 15_000;

let inFlight = false;

const reconnect = () => void reconnectCompanionWithFeedback();

function showFailure(reason: "reconnectFailed" | "stillDisconnected"): void {
  const escalated = useCompanionStore.getState().restartEscalated;
  showCompanionDisconnectToast(
    {
      level: "error",
      description: () =>
        translate(toastsMessages, escalated ? "reconnectEscalated" : reason),
    },
    reconnect,
  );
}

export function isCompanionReconnectInFlight(): boolean {
  return inFlight;
}

export async function reconnectCompanionWithFeedback(): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  try {
    showCompanionDisconnectToast(
      {
        level: "info",
        description: () => translate(toastsMessages, "reconnecting"),
      },
      reconnect,
    );
    if (!(await restartCompanion())) {
      useCompanionStore.getState().recordRestartFailure();
      showFailure("reconnectFailed");
      return;
    }
    // The IPC returns once the subprocess is up, not when Django accepted it.
    if (await waitForCompanionConnected(VERIFY_WINDOW_MS)) {
      useCompanionStore.getState().recordRestartRecovered();
      clearCompanionDisconnectToast();
      return;
    }
    useCompanionStore.getState().recordRestartFailure();
    showFailure("stillDisconnected");
  } finally {
    inFlight = false;
    rearmCompanionAutoReconnect();
  }
}
